/**
 * Station Module
 * Handles all station-related functionality
 * - Load stations data
 * - Render station cards
 * - Handle station selection
 * - Filter stations
 */

const StationModule = (() => {
  // Private variables
  let _state = {
    currentStationId: null,
    currentDeviceId: null,
    currentStationType: null,
    stations: {
      awlr: [],
      tmat: [],
      arr: [],
      awlrArr: [],
      aws: [],
      flowMeter: [],
      waterQuality: [],
      vibratingWire: [],
    },
    loadedTabs: {},
    loadingTabs: {},
    activeTab:
      localStorage.getItem(DashboardConstants.STORAGE_KEYS.ACTIVE_TAB) ||
      "tab-awlr",
  };

  /** Tabs currently loading in soft-refresh mode (no shimmer / auto-select). */
  const _softTabLoads = new Set();
  let _leftbarScrollTop = null;

  const _tabLoaders = {
    "tab-awlr": () => StationModule.loadAwlrStations(),
    "tab-tmat": () => StationModule.loadTmatStations(),
    "tab-ow": () => StationModule.loadOwStations(),
    "tab-arr": () => StationModule.loadArrStations(),
    "tab-awlr-arr": () => StationModule.loadAwlrArrStations(),
    "tab-aws": () => StationModule.loadAwsStations(),
    "tab-fm": () => StationModule.loadFlowMeterStations(),
    "tab-wq": () => StationModule.loadWaterQualityStations(),
    "tab-avwr": () => StationModule.loadVibratingWireStations(),
  };

  const _TAB_CARD_CLASS = {
    "tab-awlr": "card-station-awlr",
    "tab-tmat": "card-station-tmat",
    "tab-ow": "card-station-ow",
    "tab-arr": "card-station-arr",
    "tab-awlr-arr": "card-station-awlr-arr",
    "tab-aws": "card-station-aws",
    "tab-fm": "card-station-fm",
    "tab-wq": "card-station-wq",
    "tab-avwr": "card-station-avwr",
  };

  const _isSoftTab = (tabId) => _softTabLoads.has(tabId);

  const _getLeftbarScrollEl = () =>
    document.querySelector(".col-left .side-bar-home") ||
    document.querySelector(".col-left .overflow-scroll");

  const _captureLeftbarScroll = () => {
    const el = _getLeftbarScrollEl();
    if (el) _leftbarScrollTop = el.scrollTop;
  };

  const _restoreLeftbarScroll = () => {
    if (_leftbarScrollTop == null) return;
    const el = _getLeftbarScrollEl();
    if (el) el.scrollTop = _leftbarScrollTop;
  };

  /** Show shimmer only on initial/manual load — soft refresh keeps current cards until data arrives. */
  const _beginTabLoad = (tabId) => {
    if (_isSoftTab(tabId)) return;
    $(`#${tabId}`).empty().html(DashboardHelpers.loadShimmerLastReading(5));
  };

  /** Empty tab for re-render; on soft refresh preserve leftbar scroll first. */
  const _prepareTabRender = (tabId) => {
    if (_isSoftTab(tabId)) _captureLeftbarScroll();
    $(`#${tabId}`).empty();
  };

  /** Re-apply selected card styles without re-triggering showDetail / chart reload. */
  const _restoreActiveCardHighlight = () => {
    if (!_state.currentStationId || !_state.currentStationType) return;
    _updateActiveCard(_state.currentStationType, _state.currentStationId, {
      syncPanels: false,
    });
  };

  const _finalizeTabRender = (tabId, cardClass) => {
    setTimeout(DashboardHelpers.adjustPadding, 100);

    if (_isSoftTab(tabId)) {
      $(`#${tabId} .animate__animated`).removeClass(
        "animate__animated animate__fadeInLeft",
      );
      _restoreActiveCardHighlight();
      requestAnimationFrame(() => _restoreLeftbarScroll());
      return;
    }

    if (!cardClass) return;
    const activeTab = localStorage.getItem(
      DashboardConstants.STORAGE_KEYS.ACTIVE_TAB,
    );
    if (activeTab === tabId) {
      _autoSelectStation(tabId, cardClass);
    }
  };

  /** Pastikan tiap deviceId hanya sekali di left-section (hindari kartu dobel). */
  const _dedupeStationsByDeviceId = (stations) => {
    const seen = new Set();
    return (stations || []).filter((station) => {
      const deviceId = String(station?.deviceId ?? station?.DeviceId ?? "")
        .trim()
        .toLowerCase();
      if (!deviceId) {
        return true;
      }
      if (seen.has(deviceId)) {
        return false;
      }
      seen.add(deviceId);
      return true;
    });
  };

  // Private methods
  const _loadFromStorage = () => {
    _state.currentStationId = localStorage.getItem(
      DashboardConstants.STORAGE_KEYS.STATION_ID,
    );
    _state.currentDeviceId = localStorage.getItem(
      DashboardConstants.STORAGE_KEYS.DEVICE_ID,
    );
  };

  const _autoSelectStation = (tabId, cardClass) => {
    // Ambil station_id yang tersimpan di local storage
    const savedStationId = localStorage.getItem(
      DashboardConstants.STORAGE_KEYS.STATION_ID,
    );

    let targetCard;

    if (savedStationId) {
      targetCard = $(`.${cardClass}[data-ts-id="${savedStationId}"]`);
    }

    // Jika tidak ketemu atau storage kosong, ambil kartu pertama di tab tersebut
    if (!targetCard || targetCard.length === 0) {
      targetCard = $(`#${tabId} .${cardClass}:first`);
    }

    if (targetCard.length > 0) {
      targetCard.trigger("click");
    }
  };

  const _saveToStorage = (stationId, deviceId, name) => {
    localStorage.setItem(DashboardConstants.STORAGE_KEYS.STATION_ID, stationId);
    localStorage.setItem(DashboardConstants.STORAGE_KEYS.DEVICE_ID, deviceId);
    localStorage.setItem(
      DashboardConstants.STORAGE_KEYS.LAST_STATION_NAME,
      name,
    );
  };

  const _attachEventListeners = () => {
    // Tab click events — lazy-load inactive tabs on first show
    $("#tab-center-left a").on("click", function () {
      const selectedTab = $(this).attr("href").replace("#", "");
      localStorage.setItem(
        DashboardConstants.STORAGE_KEYS.ACTIVE_TAB,
        selectedTab,
      );
      _state.activeTab = selectedTab;
      StationModule.ensureTabLoaded(selectedTab);
      setTimeout(DashboardHelpers.adjustPadding, 100);
    });

    // AWS sensor change
    $("#aws-sensors").on("change", () => {
      _handleSensorChange("aws");
    });

  };

  const _showDetailChoiceDialog = (name, slug) => {
    Swal.fire({
      title: "Lokasi Tidak Tersedia",
      text: `Stasiun ${name} tidak memiliki koordinat di peta, atau marker belum dimuat.`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Ke Detail",
      cancelButtonText: "Tidak",
      heightAuto: false,
    }).then((result) => {
      if (result.isConfirmed && slug) {
        window.open(`/Station/Detail/${slug}`, "_blank");
      }
      if (typeof MapModule !== "undefined") {
        MapModule.triggerResize();
      }
    });
  };

  const _handleSensorChange = (type = "aws") => {
    if (!_state.currentDeviceId) return;

    const sensor = $("#aws-sensors").val();
    const chartData = {
      deviceId: _state.currentDeviceId,
      type: type,
      sensor: sensor,
    };

    ChartModule.loadData(chartData);
  };

  const _isSensorFlagEnabled = (data, flagKey, altKeys = []) => {
    const keys = [flagKey, ...(altKeys || [])];
    return keys.some((key) => data && data[key] === true);
  };

  const _displaySensorValue = (value) =>
    value == null || value === "" ? "-" : value;

  const _displaySensorWithUnit = (value, unit) => {
    const display = _displaySensorValue(value);
    return display === "-" ? "-" : `${display}${unit}`;
  };

  const _buildWqSensorKeys = (data) => {
    const keys = [];
    if (_isSensorFlagEnabled(data, "isTemperature")) keys.push("temperature");
    if (_isSensorFlagEnabled(data, "isPh", ["isPH"])) keys.push("ph");
    if (_isSensorFlagEnabled(data, "isOrp")) keys.push("orp");
    if (_isSensorFlagEnabled(data, "isTurbidity")) keys.push("turbidity");
    if (_isSensorFlagEnabled(data, "isCod")) keys.push("cod");
    if (_isSensorFlagEnabled(data, "isSalinity")) keys.push("salinity");
    if (_isSensorFlagEnabled(data, "isDo")) keys.push("do");
    if (_isSensorFlagEnabled(data, "isAmmonium")) keys.push("ammonium");
    if (_isSensorFlagEnabled(data, "isAmmonia")) keys.push("ammonia");
    if (_isSensorFlagEnabled(data, "isWaterLevel")) {
      if (DashboardServerConfig.organizationCode === "ORG007") {
        keys.push("waterLevel1", "waterLevel2");
      } else {
        keys.push("waterLevel");
      }
    }
    return keys;
  };

  /** Sensor AWS enabled → value option di #aws-sensors (chart). */
  const AWS_CHART_SENSOR_OPTIONS = [
    { value: "humidity", label: "Kelembaban", key: "humidity" },
    { value: "rainfall", label: "Curah Hujan", key: "rainfall" },
    { value: "temperature", label: "Suhu", key: "temperature" },
    { value: "pressure", label: "Tekanan", key: "pressure" },
    { value: "evaporation", label: "Penguapan", key: "evaporation" },
    { value: "winddirection", label: "Arah Angin", key: "winddirection" },
    { value: "windspeed", label: "Kecepatan Angin", key: "windspeed" },
    { value: "solarradiation", label: "Radiasi Matahari", key: "solarradiation" },
    { value: "uv", label: "Ultraviolet", key: "uv" },
  ];

  const _buildAwsSensorKeys = (data) => {
    const keys = [];
    if (_isSensorFlagEnabled(data, "isHumidity")) keys.push("humidity");
    if (_isSensorFlagEnabled(data, "isRainfall")) keys.push("rainfall");
    if (_isSensorFlagEnabled(data, "isPressure")) keys.push("pressure");
    if (_isSensorFlagEnabled(data, "isSolarRadiation")) keys.push("solarradiation");
    if (_isSensorFlagEnabled(data, "isTemperature")) keys.push("temperature");
    if (_isSensorFlagEnabled(data, "isWindDirection")) keys.push("winddirection");
    if (_isSensorFlagEnabled(data, "isWindSpeed")) keys.push("windspeed");
    if (_isSensorFlagEnabled(data, "isEvaporation")) keys.push("evaporation");
    if (_isSensorFlagEnabled(data, "isUv")) keys.push("uv");
    return keys;
  };

  /** Sync dropdown grafik AWS agar hanya sensor enabled (sama seperti card/popup). */
  const _syncAwsSensorSelect = (enabledKeys) => {
    const $select = $("#aws-sensors");
    if (!$select.length) return;

    // null/undefined = jangan filter (legacy). array (meski kosong) = filter ketat.
    if (!Array.isArray(enabledKeys)) return;

    const enabled = new Set(
      enabledKeys
        .map((k) => String(k).toLowerCase().replace(/_/g, ""))
        .filter(Boolean),
    );
    const previous = String($select.val() || "").toLowerCase();
    const options = AWS_CHART_SENSOR_OPTIONS.filter((opt) =>
      enabled.has(opt.key),
    );

    $select.empty();
    if (!options.length) {
      $select.hide();
      return;
    }

    options.forEach((opt) => {
      $select.append(
        $("<option>", { value: opt.value, text: opt.label }),
      );
    });

    const stillValid = options.some((opt) => opt.value === previous);
    $select.val(stillValid ? previous : options[0].value);
  };

  const _updateActiveCard = (type, stationId, options = {}) => {
    const syncPanels = options.syncPanels !== false;

    // Remove all active classes (include #tab-ow so only one OW card stays selected)
    $(
      "#tab-awlr .card-awlr, #tab-tmat .card-awlr, #tab-ow .card-awlr, #tab-awlr-arr .card-awlr, #tab-aws .card-awlr, #tab-arr .card-arr, #tab-fm .card-fm, #tab-wq .card-wq, #tab-avwr .card-awlr",
    ).removeClass("card-awlr card-arr card-fm card-wq");

    // Hide/show right-panel controls only on real selection (not soft leftbar refresh)
    if (syncPanels) {
      $("#aws-sensors").fadeOut(50);
      $("#parent-chart-ch").fadeOut(50);
    }

    let idCard = "";
    let activeClass = ""; // ✅ Tambahkan variabel untuk class yang sesuai

    switch (type.toUpperCase()) {
      case DashboardConstants.STATION_TYPES.AWLR:
        idCard = `card-station-awlr-${stationId}`;
        activeClass = "card-awlr";
        break;

      case DashboardConstants.STATION_TYPES.TMAT:
      case DashboardConstants.STATION_TYPES.AGWLR:
        idCard = `card-station-tmat-${stationId}`;
        activeClass = "card-awlr";
        break;

      case DashboardConstants.STATION_TYPES.OW:
        idCard = `card-station-ow-${stationId}`;
        activeClass = "card-awlr";
        break;

      case DashboardConstants.STATION_TYPES.AWLR_ARR:
        if (syncPanels) $("#parent-chart-ch").fadeIn(100);
        idCard = `card-station-awlr-arr-${stationId}`;
        activeClass = "card-awlr"; // ✅
        break;

      case DashboardConstants.STATION_TYPES.ARR:
        idCard = `card-station-arr-${stationId}`;
        activeClass = "card-arr"; // ✅ Ini yang penting!
        break;

      case DashboardConstants.STATION_TYPES.AWS: {
        if (syncPanels) {
          const $awsCard = $(`#card-station-aws-${stationId}`);
          const awsSensorsRaw =
            $awsCard.attr("data-aws-sensors") ||
            $awsCard.data("awsSensors") ||
            "";
          const awsSensors =
            typeof awsSensorsRaw === "string" && awsSensorsRaw.length > 0
              ? awsSensorsRaw.split(",").map((s) => s.trim()).filter(Boolean)
              : [];
          _syncAwsSensorSelect(awsSensors);
          $("#aws-sensors").fadeIn(100);
        }
        idCard = `card-station-aws-${stationId}`;
        activeClass = "card-awlr"; // ✅
        break;
      }

      case DashboardConstants.STATION_TYPES.FM:
        idCard = `card-station-fm-${stationId}`;
        activeClass = "card-fm"; // ✅
        break;

      case DashboardConstants.STATION_TYPES.WQ:
      case "WQMS":
        idCard = `card-station-wq-${stationId}`;
        activeClass = "card-wq";
        break;

      case DashboardConstants.STATION_TYPES.AVWR:
        idCard = `card-station-avwr-${stationId}`;
        activeClass = "card-awlr"; // ✅ Reuse card-awlr styling
        break;
    }

    if (idCard && activeClass) {
      $(`#${idCard}`)
        .find(".card.card-station, .card-header, .card-body, .card-footer")
        .addClass(activeClass);
    }
  };


  /**
   * Compact meta badges under station name (brand, device id, battery).
   * Shared by all station card types.
   */
  const _renderStationMetaBadges = (data) => {
    const brand = data.brandName != null ? data.brandName : "Unknown Brand";
    const deviceId = data.deviceId != null ? data.deviceId : "-";
    let batteryBadge = "";

    if (
      typeof DashboardServerConfig !== "undefined" &&
      DashboardServerConfig.powerSource == "B" &&
      data.battery != null
    ) {
      const batteryClass =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      batteryBadge = `
        <span class="dc-meta-badge dc-meta-badge--battery" title="Battery">
          <i class="mdi ${batteryClass}"></i> ${DashboardHelpers.formatNumber(data.battery)} V
        </span>`;
    }

    return `
      <div class="dc-station-meta">
        <span class="dc-meta-badge dc-meta-badge--brand">${brand}</span>
        <span class="dc-meta-badge dc-meta-badge--device">${deviceId}</span>
        ${batteryBadge}
      </div>`;
  };

  /**
   * Compact card header: name + time, meta badges under name.
   */
  const _renderStationCardHeader = (data, deviceStatusHtml, readingAtHtml) => {
    return `
      <div class="card-header">
        <div class="dc-station-header">
          <div class="dc-station-title-block">
            <h4 class="card-title text-uppercase text-station-name m-0">
              ${deviceStatusHtml} ${data.name}
            </h4>
            ${_renderStationMetaBadges(data)}
          </div>
          <span class="reading-at">${readingAtHtml || ""}</span>
        </div>
      </div>`;
  };

  const _generateAwlrCardContent = (data) => {
    let deviceStatus =
      '<small class="mdi mdi-circle text-danger me-1"></small>';

    if (data.readingAt != null) {
      const readingAt = moment.parseZone(data.readingAt);
      const currentTime = moment();
      const diffInHours = currentTime.diff(readingAt, "hours", true);

      if (diffInHours <= DashboardConstants.ONLINE_THRESHOLD.HOURS) {
        deviceStatus =
          '<small class="mdi mdi-circle text-success me-1"></small>';
      }
    }

    // Warning status badge
    const warningStatus = DashboardHelpers.getWarningStatusBadge(
      data.warningStatus,
    );

    // Change status icon (increase/decrease)
    let changeIcon = "";
    if (data.changeStatus === "inc") {
      changeIcon = '<i class="mdi mdi-arrow-up-circle text-danger me-1"></i>';
    } else if (data.changeStatus === "dec") {
      changeIcon =
        '<i class="mdi mdi-arrow-down-circle text-success me-1"></i>';
    }

    // Water level
    const waterLevel =
      data.waterLevel != null
        ? `${changeIcon}${DashboardHelpers.formatNumber(
            data.waterLevel,
          )} <sup>${data.unitDisplay}</sup>`
        : "-";

    // Debit
    const debit =
      data.debit != null
        ? `${DashboardHelpers.formatNumber(data.debit)} <sup>m³/s</sup>`
        : "-";

    // Battery status
    let battery = "";
    if (data.battery != null) {
      const batteryClass =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryClass} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // Reading time
    const readingAt = DashboardHelpers.formatReadingTime(
      data.readingAt,
      data.timeZone,
    );

    // Warning status bar (if siaga levels are set)
    let warningStatusBar = "";
    if (data.siaga1 != null && data.siaga2 != null && data.siaga3 != null) {
      warningStatusBar = `
                <div class="progress-meter mt-2 mb-2">
                    <div class="meter meter-normal meter-left" style="width: 25%;">
                        <span class="fw-normal meter-text" style="color: #4DC27E;">0</span>
                    </div>
                    <div class="meter meter-waspada meter-left" style="width: 25%;">
                        <span class="fw-normal meter-text" style="color: #FFDA4F;">${DashboardHelpers.formatNumber(data.siaga3)} ${data.unitDisplay}</span>
                    </div>
                    <div class="meter meter-siaga meter-left" style="width: 25%;">
                        <span class="fw-normal meter-text" style="color: #FFA600;">${DashboardHelpers.formatNumber(data.siaga2)} ${data.unitDisplay}</span>
                    </div>
                    <div class="meter meter-awas meter-left" style="width: 25%;">
                        <span class="fw-normal meter-text" style="color: #EF5350;">${DashboardHelpers.formatNumber(data.siaga1)} ${data.unitDisplay}</span>
                    </div>
                </div>
            `;
    }

    // Card footer (if offline)
    let cardFooter = "";
    if (data.deviceStatus === "offline") {
      const message =
        data.readingAt == null
          ? "Alat belum mengirim data"
          : "Alat tidak aktif lebih dari 24 jam";

      cardFooter = `
                <div class="card-footer">
                    <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
                        <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
                        ${message}
                    </small>
                </div>
            `;
    }

    if (DashboardServerConfig.organizationCode == "ORG081") {
      return `
            <div onclick="StationModule.showDetail(this, event)" 
                 class="card-station-awlr" 
                 data-ts-id="${data.id}" 
                 id="card-station-awlr-${data.id}" 
                 data-last-update="${
                   data.readingAt
                     ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
                     : ""
                 }" 
                 data-name="${data.name}" 
                 data-slug="${data.slug}" 
                 data-device="${data.deviceId}" 
                 data-type="${data.type}"
                 data-siaga1="${data.siaga1 ?? ""}"
                 data-siaga2="${data.siaga2 ?? ""}" 
                 data-siaga3="${data.siaga3 ?? ""}"
                 data-lat="${data.latitude}"
                 data-lng="${data.longitude}"
                 data-cross-section="${DashboardHelpers.parseHasCrossSection(data.hasCrossSection) ? "true" : "false"}"
                 >
                <div class="card card-station animate__animated animate__fadeInLeft">
                                        ${_renderStationCardHeader(data, deviceStatus, readingAt)}
                    <div class="card-body">
                                                <div class="d-flex justify-content-around align-items-center reading-text">
                            <div class="col-4 text-center">
                                <h4 class="text-status-${
                                  warningStatus.class
                                }">${waterLevel}</h4>
                            </div>
                            <div class="col-4 text-center">
                                <h4></h4>
                            </div>
                            <div class="col-4 text-center">
                                <h4>${warningStatus.html}</h4>
                            </div>
                        </div>
                        <div class="d-flex justify-content-around reading-text">
                            <div class="col-4 text-center">
                                ${DashboardHelpers.getAwlrLevelFullLabel(
                                  data.type,
                                )}
                            </div>
                            <div class="col-4 text-center"></div>
                            <div class="col-4 text-center">Status Siaga</div>
                        </div>
                        ${warningStatusBar}
                    </div>
                    ${cardFooter}
                </div>
            </div>
        `;
    } else {
      return `
            <div onclick="StationModule.showDetail(this, event)" 
                 class="card-station-awlr" 
                 data-ts-id="${data.id}" 
                 id="card-station-awlr-${data.id}" 
                 data-last-update="${
                   data.readingAt
                     ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
                     : ""
                 }" 
                 data-name="${data.name}" 
                 data-slug="${data.slug}" 
                 data-device="${data.deviceId}" 
                 data-type="${data.type}"
                 data-siaga1="${data.siaga1 ?? ""}"
                 data-siaga2="${data.siaga2 ?? ""}" 
                 data-siaga3="${data.siaga3 ?? ""}"
                 data-lat="${data.latitude}"
                 data-lng="${data.longitude}"
                 data-cross-section="${DashboardHelpers.parseHasCrossSection(data.hasCrossSection) ? "true" : "false"}"
                 >
                <div class="card card-station animate__animated animate__fadeInLeft">
                                        ${_renderStationCardHeader(data, deviceStatus, readingAt)}
                    <div class="card-body">
                                                <div class="d-flex justify-content-around align-items-center reading-text">
                            <div class="col-4 text-center">
                                <h4 class="text-status-${
                                  warningStatus.class
                                }">${waterLevel}</h4>
                            </div>
                            <div class="col-4 text-center">
                                <h4>${debit}</h4>
                            </div>
                            <div class="col-4 text-center">
                                <h4>${warningStatus.html}</h4>
                            </div>
                        </div>
                        <div class="d-flex justify-content-around reading-text">
                            <div class="col-4 text-center">
                                ${DashboardHelpers.getAwlrLevelFullLabel(
                                  data.type,
                                )}
                            </div>
                            <div class="col-4 text-center">Debit</div>
                            <div class="col-4 text-center">Status Siaga</div>
                        </div>
                        ${warningStatusBar}
                    </div>
                    ${cardFooter}
                </div>
            </div>
        `;
    }
  };

  const _generateTmatCardContent = (data) => {
    const cardType = DashboardConstants.STATION_TYPES.AGWLR;

    let deviceStatus =
      '<small class="mdi mdi-circle text-danger me-1"></small>';

    if (data.readingAt != null) {
      const readingAt = moment.parseZone(data.readingAt);
      const currentTime = moment();
      const diffInHours = currentTime.diff(readingAt, "hours", true);

      if (diffInHours <= DashboardConstants.ONLINE_THRESHOLD.HOURS) {
        deviceStatus =
          '<small class="mdi mdi-circle text-success me-1"></small>';
      }
    }

    // Warning status badge
    const warningStatus = DashboardHelpers.getWarningStatusBadge(
      data.warningStatus,
    );

    // Change status icon (increase/decrease)
    let changeIcon = "";
    if (data.changeStatus === "inc") {
      changeIcon = '<i class="mdi mdi-arrow-up-circle text-danger me-1"></i>';
    } else if (data.changeStatus === "dec") {
      changeIcon =
        '<i class="mdi mdi-arrow-down-circle text-success me-1"></i>';
    }

    // Water level
    const waterLevel =
      data.waterLevel != null
        ? `${DashboardHelpers.formatNumber(
            data.waterLevel,
          )} <sup>${data.unitDisplay}</sup>`
        : "-";

    // Debit
    const debit =
      data.debit != null
        ? `${DashboardHelpers.formatNumber(data.debit)} <sup>m³/s</sup>`
        : "-";

    // Battery status
    let battery = "";
    if (data.battery != null) {
      const batteryClass =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryClass} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // Reading time
    const readingAt = DashboardHelpers.formatReadingTime(
      data.readingAt,
      data.timeZone,
    );

    if(DashboardServerConfig.organizationCode == "ORG084") {
      return `
            <div onclick="StationModule.showDetail(this, event)" 
                 class="card-station-tmat" 
                 data-ts-id="${data.id}" 
                 id="card-station-tmat-${data.id}" 
                 data-last-update="${
                   data.readingAt
                     ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
                     : ""
                 }" 
                 data-name="${data.name}" 
                 data-slug="${data.slug}" 
                 data-device="${data.deviceId}" 
                 data-type="${cardType}"
                 data-siaga1="${data.siaga1 ?? ""}"
                 data-siaga2="${data.siaga2 ?? ""}" 
                 data-siaga3="${data.siaga3 ?? ""}"
                 data-lat="${data.latitude}"
                 data-lng="${data.longitude}"
                 data-well-depth="${data.wellDepth}"
                 data-well-depth-unit="${"m"}"
                 data-well-elevation="${data.wellElevation}"
                 >
                <div class="card card-station animate__animated animate__fadeInLeft">
                                        ${_renderStationCardHeader(data, deviceStatus, readingAt)}
                    <div class="card-body">
                                                <div class="d-flex justify-content-around align-items-center reading-text">
                            <div class="col-4 text-center">
                                <h4>${waterLevel}</h4>
                            </div>
                            <div class="col-4 text-center">
                              <h4>
                                  ${
                                    DashboardHelpers.isAgwlr(cardType)
                                      ? data.wellElevation === null
                                        ? "-"
                                        : `${DashboardHelpers.formatNumber(Number(data.wellElevation) - Number(data.waterLevel))} <sup>mdpl</sup>`
                                      : debit
                                  }
                              </h4>
                            </div>
                        </div>

                        <div class="d-flex justify-content-around reading-text">
                            <div class="col-4 text-center">
                                ${DashboardHelpers.getAwlrLevelFullLabel(
                                  cardType,
                                )}
                            </div>
                            <div class="col-4 text-center">
                                ${DashboardHelpers.getAwlrElevationLabel(cardType)}
                            </div>
                            
                        </div>

                        <div class="d-flex justify-content-around align-items-center reading-text mt-1">
                            <div class="col-4 text-center">
                                <h4 class="mb-0">${DashboardHelpers.formatNumber(data.ph)}</h4>
                            </div>
                            <div class="col-4 text-center">
                                <h4>${DashboardHelpers.formatNumber(data.temperature)} <sup>°C</sup> </h4>
                            </div>
                        </div>
                        <div class="d-flex justify-content-around reading-text">
                            <div class="col-4 text-center">
                                pH
                            </div>
                            <div class="col-4 text-center">
                                Suhu
                            </div>
                            
                        </div>
                      
                    </div>
                   
                </div>
            </div>
        `;
    }else{
      return `
            <div onclick="StationModule.showDetail(this, event)" 
                 class="card-station-tmat" 
                 data-ts-id="${data.id}" 
                 id="card-station-tmat-${data.id}" 
                 data-last-update="${
                   data.readingAt
                     ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
                     : ""
                 }" 
                 data-name="${data.name}" 
                 data-slug="${data.slug}" 
                 data-device="${data.deviceId}" 
                 data-type="${cardType}"
                 data-siaga1="${data.siaga1 ?? ""}"
                 data-siaga2="${data.siaga2 ?? ""}" 
                 data-siaga3="${data.siaga3 ?? ""}"
                 data-lat="${data.latitude}"
                 data-lng="${data.longitude}"
                 data-well-depth="${data.wellDepth}"
                 data-well-depth-unit="${"m"}"
                 data-well-elevation="${data.wellElevation}"
                 >
                <div class="card card-station animate__animated animate__fadeInLeft">
                                        ${_renderStationCardHeader(data, deviceStatus, readingAt)}
                    <div class="card-body">
                                                <div class="d-flex justify-content-around align-items-center reading-text">
                            <div class="col-4 text-center">
                                <h4>${waterLevel}</h4>
                            </div>
                            <div class="col-4 text-center">
                              <h4>
                                  ${
                                    DashboardHelpers.isAgwlr(cardType)
                                      ? data.wellElevation === null
                                        ? "-"
                                        : `${DashboardHelpers.formatNumber(Number(data.wellElevation) - Number(data.waterLevel))} <sup>mdpl</sup>`
                                      : debit
                                  }
                              </h4>
                            </div>
                        </div>
                        <div class="d-flex justify-content-around reading-text">
                            <div class="col-4 text-center">
                                ${DashboardHelpers.getAwlrLevelFullLabel(
                                  cardType,
                                )}
                            </div>
                            <div class="col-4 text-center">
                                ${DashboardHelpers.getAwlrElevationLabel(cardType)}
                            </div>
                            
                        </div>
                      
                    </div>
                   
                </div>
            </div>
        `;
    }

    
  };

  const _generateOwCardContent = (data) => {
    const cardType = DashboardConstants.STATION_TYPES.OW;
    let deviceStatus =
      '<small class="mdi mdi-circle text-danger me-1"></small>';

    if (data.readingAt != null) {
      const readingAtMoment = moment.parseZone(data.readingAt);
      const diffInHours = moment().diff(readingAtMoment, "hours", true);
      if (diffInHours <= DashboardConstants.ONLINE_THRESHOLD.HOURS) {
        deviceStatus =
          '<small class="mdi mdi-circle text-success me-1"></small>';
      }
    }

    const waterLevel =
      data.waterLevel != null
        ? `${DashboardHelpers.formatNumber(data.waterLevel)} <sup>${data.unitDisplay || "m"}</sup>`
        : "-";
    const elevation =
      data.waterLevelElevation != null
        ? `${DashboardHelpers.formatNumber(data.waterLevelElevation)} <sup>mdpl</sup>`
        : "-";
    let battery = "";
    if (data.battery != null) {
      const batteryClass =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryClass} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }
    const readingAt = DashboardHelpers.formatReadingTime(
      data.readingAt,
      data.timeZone,
    );

    return `
      <div onclick="StationModule.showDetail(this, event)"
           class="card-station-ow"
           data-ts-id="${data.id}"
           id="card-station-ow-${data.id}"
           data-last-update="${data.readingAt ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss") : ""}"
           data-name="${data.name}"
           data-slug="${data.slug}"
           data-device="${data.deviceId}"
           data-type="${cardType}"
           data-lat="${data.latitude}"
           data-lng="${data.longitude}"
           data-well-depth="${data.wellDepth ?? ""}"
           data-well-elevation="${data.wellElevation ?? ""}">
        <div class="card card-station animate__animated animate__fadeInLeft">
          ${_renderStationCardHeader(data, deviceStatus, readingAt)}
          <div class="card-body">
            <div class="d-flex justify-content-around align-items-center reading-text">
              <div class="col-4 text-center"><h4>${waterLevel}</h4></div>
              <div class="col-4 text-center"><h4>${elevation}</h4></div>
            </div>
            <div class="d-flex justify-content-around reading-text">
              <div class="col-4 text-center">Water Level</div>
              <div class="col-4 text-center">Elevasi</div>
            </div>
            ${battery ? `<div class="text-center mt-1">${battery}</div>` : ""}
          </div>
        </div>
      </div>`;
  };

  const _generateArrCardContent = (data) => {
    // Device status
    let deviceStatus =
      '<small class="mdi mdi-circle text-danger me-1"></small>';

    if (data.readingAt != null) {
      if (DashboardHelpers.checkOnlineStatus(data.readingAt)) {
        deviceStatus =
          '<small class="mdi mdi-circle text-success me-1"></small>';
      }
    }

    // Rainfall and intensity
    const rainfall =
      data.rainfallLastHour != null
        ? `${DashboardHelpers.formatNumber(
            data.rainfallLastHour,
          )} <sup>mm</sup>`
        : "-";

    const intensity =
      data.intensityLastHour != null
        ? DashboardHelpers.getIntensityIconMap(data.intensityLastHour)
        : "-";

    const intensityName =
      data.intensityLastHour != null
        ? DashboardHelpers.getIntensityName(data.intensityLastHour)
        : "-";

    // Battery
    let battery = "";
    if (data.battery != null) {
      const batteryClass =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><small class="mdi ${batteryClass} ms-1" style="color: #F69696;"></small> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // Reading time
    const readingAt = DashboardHelpers.formatReadingTime(
      data.readingAt,
      data.timeZone,
    );

    // Blinking effect for rain
    const blinkingClass =
      data.intensityLastHour != 0 && data.deviceStatus === "online"
        ? "blinking-content"
        : "";

    // Card footer
    let cardFooter = "";
    if (data.deviceStatus === "offline") {
      const message =
        data.readingAt == null
          ? "Alat belum mengirim data"
          : "Alat tidak aktif lebih dari 24 jam";

      cardFooter = `
                <div class="card-footer">
                    <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
                        <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
                        ${message}
                    </small>
                </div>
            `;
    }

    return `
            <div onclick="StationModule.showDetail(this, event)" 
                 class="card-station-arr" 
                 data-ts-id="${data.id}" 
                 id="card-station-arr-${data.id}" 
                 data-last-update="${
                   data.readingAt
                     ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
                     : ""
                 }" 
                 data-name="${data.name}" 
                 data-slug="${data.slug}" 
                 data-device="${data.deviceId}" 
                 data-lat="${data.latitude}"
                 data-lng="${data.longitude}"
                 data-type="ARR">
                <div class="card card-station animate__animated animate__fadeInLeft">
                                        ${_renderStationCardHeader(data, deviceStatus, readingAt)}
                    <div class="card-body">
                                                <div class="d-flex justify-content-around align-items-center reading-text mb-1">
                            <div class="col-4 text-center">
                                <h4 class="text-range-hour">
                                    ${
                                      data.readingAt
                                        ? moment
                                            .parseZone(data.readingAt)
                                            .format("HH:mm")
                                        : "-"
                                    }
                                </h4>
                            </div>
                            <div class="col-4 text-center">
                                <h4>${rainfall}</h4>
                            </div>
                            <div class="col-4 col-intensity text-center">
                                <div class="${blinkingClass}">${intensity}</div>
                            </div>
                        </div>
                        <div class="d-flex justify-content-around reading-text">
                            <div class="col-4 text-center">Waktu (${
                              data.timeZone ?? "WIB"
                            })</div>
                            <div class="col-4 text-center">Curah Hujan</div>
                            <div class="col-4 text-center fst-italic">${intensityName}</div>
                        </div>
                    </div>
                    ${cardFooter}
                </div>
            </div>
        `;
  };

  const _generateAwlrArrCardContent = (data) => {
    // 🔹 Device status
    let deviceStatus = `<small class="mdi mdi-circle text-danger me-1"></small>`;

    if (data.readingAt != null) {
      const readingAt = moment.parseZone(data.readingAt);
      const currentTime = moment.parseZone();
      const diffInHours = currentTime.diff(readingAt, "hours");
      if (diffInHours < 12) {
        deviceStatus = `<small class="mdi mdi-circle text-success me-1"></small>`;
      }
    }

    // 🔹 Battery
    let battery = "";
    if (data.battery != null) {
      const batteryIcon =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryIcon} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // 🔹 Water Level & Elevation
    const wlOperator = data.waterLevelElevation > 0 ? "+" : "";
    const wlElevation =
      data.waterLevelElevation != null
        ? `${wlOperator}${data.waterLevelElevation} <sup>mdpl</sup>`
        : "-";
    const wlValue =
      data.waterLevel != null
        ? `${DashboardHelpers.formatNumber(data.waterLevel)} <sup>${data.unitDisplay}</sup>`
        : "-";

    // 🔹 Debit
    const debit =
      data.debit != null ? `${data.debit} <sup>m<sup>3</sup>/s</sup>` : "-";

    // 🔹 Status Siaga
    let warningStatusHtml = `<span class="badge badge-awlr-nostatus rounded-1 ms-1 align-self-center">TANPA STATUS</span>`;
    let warningClass = "";

    switch (data.warningStatus) {
      case 0:
        warningStatusHtml = `<span class="badge badge-awlr-normal rounded-1 ms-1 align-self-center">NORMAL</span>`;
        warningClass = "normal";
        break;
      case 3:
        warningStatusHtml = `<span class="badge badge-awlr-siaga3 rounded-1 ms-1 align-self-center blinking-content">WASPADA</span>`;
        warningClass = "siaga3";
        break;
      case 2:
        warningStatusHtml = `<span class="badge badge-awlr-siaga2 rounded-1 ms-1 align-self-center blinking-content">SIAGA</span>`;
        warningClass = "siaga2";
        break;
      case 1:
        warningStatusHtml = `<span class="badge badge-awlr-siaga1 rounded-1 ms-1 align-self-center blinking-content">AWAS</span>`;
        warningClass = "siaga1";
        break;
    }

    // 🔹 Curah Hujan & Intensitas
    let intensityPerHour = `
        <div class="d-flex justify-content-around align-items-center reading-text mb-1">
            <div class="col-4 text-center">-</div>
            <div class="col-4 text-center">-</div>
        </div>
        <div class="d-flex justify-content-around reading-text">
            <div class="col-4 text-center">Curah Hujan</div>
            <div class="col-4 text-center fst-italic">Intensitas</div>
        </div>
    `;

    if (data.rainfallLastHour != null && data.intensityLastHour != null) {
      const blinking =
        data.intensityLastHour !== 0 && data.deviceStatus === "online"
          ? "blinking-content"
          : "";
      const intensityName = DashboardHelpers.getIntensityName(
        data.intensityLastHour,
      );
      const icon = DashboardHelpers.getIntensityIcon(data.intensityLastHour);

      intensityPerHour = `
            <div class="d-flex justify-content-around align-items-center reading-text mt-1 mb-1">
                <div class="col-4 text-center">
                    <h4>${DashboardHelpers.formatNumber(data.rainfallLastHour)} <sup>mm</sup></h4>
                </div>
                <div class="col-4 text-center"></div>
                <div class="col-4 col-intensity text-center">
                    <img class="${blinking}" src="/images/arr/${icon}" alt="${
                      data.intensityLastHour
                    }">
                </div>
            </div>
            <div class="d-flex justify-content-around reading-text">
                <div class="col-4 text-center">Curah Hujan</div>
                <div class="col-4 text-center"></div>
                <div class="col-4 text-center fst-italic">${intensityName}</div>
            </div>
        `;
    }

    // 🔹 Warning bar (siaga 1–3)
    let warningStatusBar = "";
    if (data.siaga1) {
      warningStatusBar = `
            <div class="progress-meter mt-2 mb-2">
                <div class="meter meter-normal meter-left" style="width: 25%;">
                    <span class="fw-normal meter-text" style="color: #4DC27E;">0</span>
                </div>
                <div class="meter meter-waspada meter-left" style="width: 25%;">
                    <span class="fw-normal meter-text" style="color: #FFDA4F;">${DashboardHelpers.formatNumber(data.siaga3)} ${data.unitDisplay}</span>
                </div>
                <div class="meter meter-siaga meter-left" style="width: 25%;">
                    <span class="fw-normal meter-text" style="color: #FFA600;">${DashboardHelpers.formatNumber(data.siaga2)} ${data.unitDisplay}</span>
                </div>
                <div class="meter meter-awas meter-left" style="width: 25%;">
                    <span class="fw-normal meter-text" style="color: #EF5350;">${DashboardHelpers.formatNumber(data.siaga1)} ${data.unitDisplay}</span>
                </div>
            </div>
        `;
    }

    // 🔹 Reading time
    let readingAt = "";
    if (data.readingAt != null) {
      const today = moment(new Date()).format("YYYY-MMM-DD");
      const readDate = moment.parseZone(data.readingAt).format("YYYY-MMM-DD");
      if (today === readDate) {
        readingAt = `Hari Ini, <span class="text-muted">${moment
          .parseZone(data.readingAt)
          .format("HH:mm")} ${data.timeZone}</span>`;
      } else {
        readingAt = moment
          .parseZone(data.readingAt)
          .locale("id")
          .format("D MMMM YYYY HH:mm");
      }
    }

    // 🔹 Footer jika offline
    let cardFooter = "";
    if (data.deviceStatus === "offline") {
      const message =
        data.readingAt == null
          ? "Alat belum mengirim data"
          : "Alat tidak aktif lebih dari 24 jam";
      cardFooter = `
            <div class="card-footer">
                <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
                    <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
                    ${message}
                </small>
            </div>
        `;
    }

    if (DashboardServerConfig.organizationCode == "ORG081") {
      // 🔹 Final return
      return `
        <div onclick="StationModule.showDetail(this, event)"
            class="card-station-awlr-arr"
            data-ts-id="${data.id}"
            id="card-station-awlr-arr-${data.id}"
            data-last-update="${
              data.readingAt
                ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
                : ""
            }"
            data-name="${data.name}"
            data-slug="${data.slug}" 
            data-device="${data.deviceId}"
            data-type="${data.type}"
            data-lat="${data.latitude}"
            data-lng="${data.longitude}"
            data-siaga1="${data.siaga1 ?? ""}" 
            data-siaga2="${data.siaga2 ?? ""}" 
            data-siaga3="${data.siaga3 ?? ""}"
            data-cross-section="${DashboardHelpers.parseHasCrossSection(data.hasCrossSection) ? "true" : "false"}"
            >
            <div class="card card-station animate__animated animate__fadeInLeft">
                                ${_renderStationCardHeader(data, deviceStatus, readingAt)}
                <div class="card-body">
                                        <div class="d-flex justify-content-around align-items-center reading-text">
                        <div class="col-4 text-center">
                            <h4 class="text-status-${warningClass}">${wlValue}</h4>
                        </div>
                        <div class="col-4 text-center"><h4></h4></div>
                        <div class="col-4 text-center"><h4>${warningStatusHtml}</h4></div>
                    </div>

                    <div class="d-flex justify-content-around reading-text">
                        <div class="col-4 text-center">Tinggi Muka Air</div>
                        <div class="col-4 text-center"></div>
                        <div class="col-4 text-center">Status Siaga</div>
                    </div>

                    ${intensityPerHour}
                    ${warningStatusBar}
                </div>
                ${cardFooter}
            </div>
        </div>
    `;
    } else {
      // 🔹 Final return
      return `
        <div onclick="StationModule.showDetail(this, event)"
            class="card-station-awlr-arr"
            data-ts-id="${data.id}"
            id="card-station-awlr-arr-${data.id}"
            data-last-update="${
              data.readingAt
                ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
                : ""
            }"
            data-name="${data.name}"
            data-slug="${data.slug}" 
            data-device="${data.deviceId}"
            data-type="${data.type}"
            data-lat="${data.latitude}"
            data-lng="${data.longitude}"
            data-siaga1="${data.siaga1 ?? ""}" 
            data-siaga2="${data.siaga2 ?? ""}" 
            data-siaga3="${data.siaga3 ?? ""}"
            data-cross-section="${DashboardHelpers.parseHasCrossSection(data.hasCrossSection) ? "true" : "false"}"
            >
            <div class="card card-station animate__animated animate__fadeInLeft">
                                ${_renderStationCardHeader(data, deviceStatus, readingAt)}
                <div class="card-body">
                                        <div class="d-flex justify-content-around align-items-center reading-text">
                        <div class="col-4 text-center">
                            <h4 class="text-status-${warningClass}">${wlValue}</h4>
                        </div>
                        <div class="col-4 text-center"><h4>${debit}</h4></div>
                        <div class="col-4 text-center"><h4>${warningStatusHtml}</h4></div>
                    </div>

                    <div class="d-flex justify-content-around reading-text">
                        <div class="col-4 text-center">Tinggi Muka Air</div>
                        <div class="col-4 text-center">Debit</div>
                        <div class="col-4 text-center">Status Siaga</div>
                    </div>

                    ${intensityPerHour}
                    ${warningStatusBar}
                </div>
                ${cardFooter}
            </div>
        </div>
    `;
    }
  };

  const _generateAwsCardContent = (data) => {
    // 🔹 Device status
    let deviceStatus = `<small class="mdi mdi-circle text-danger me-1"></small>`;

    if (data.readingAt != null) {
      const readingAt = moment.parseZone(data.readingAt);
      const currentTime = moment.parseZone();
      const diffInHours = currentTime.diff(readingAt, "hours");

      if (diffInHours <= 12) {
        deviceStatus = `<small class="mdi mdi-circle text-success me-1"></small>`;
      }
    }

    // 🔹 Battery
    let battery = "";
    if (data.battery != null) {
      const batteryIcon =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryIcon} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // 🔹 Sensor Data — tampilkan sensor yang di-set true di PostgreSQL, nilai null → "-"
    const humidity = _isSensorFlagEnabled(data, "isHumidity")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Kelembapan :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.humidity, "%")}</span>
        </div>
      </div>`
      : "";

    const pressure = _isSensorFlagEnabled(data, "isPressure")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Tekanan :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.pressure, " MB")}</span>
        </div>
      </div>`
      : "";

    const windDirection = _isSensorFlagEnabled(data, "isWindDirection")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Arah Angin :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">
            ${
              data.windDirection == null
                ? "-"
                : `<span style="transform: rotate(${data.windDirection}deg);"><i class="mdi mdi-navigation"></i></span>
            ${data.windDirection} <sup>&deg;</sup>`
            }
          </span>
        </div>
      </div>`
      : "";

    const solarRadiation = _isSensorFlagEnabled(data, "isSolarRadiation")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Radiasi Matahari :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.solarRadiation, " W/m2")}</span>
        </div>
      </div>`
      : "";

    const temperature = _isSensorFlagEnabled(data, "isTemperature")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Suhu :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.temperature, " &deg;C")}</span>
        </div>
      </div>`
      : "";

    const windSpeed = _isSensorFlagEnabled(data, "isWindSpeed")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Kec. Angin :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.windSpeed, " Km/jam")}</span>
        </div>
      </div>`
      : "";

    const evaporation = _isSensorFlagEnabled(data, "isEvaporation")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Penguapan :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.evaporation, " mm")}</span>
        </div>
      </div>`
      : "";

    const uv = _isSensorFlagEnabled(data, "isUv")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">UV :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorValue(data.uv)}</span>
        </div>
      </div>`
      : "";

    // 🔹 Rainfall & Intensity
    let rainfall = "";
    if (_isSensorFlagEnabled(data, "isRainfall")) {
      const rainfallValue = _displaySensorValue(data.rainfallLastHour);
      const intensityName =
        data.intensityLastHour != null
          ? DashboardHelpers.getIntensityName(data.intensityLastHour)
          : "-";
      const blinking =
        data.intensityLastHour &&
        data.intensityLastHour !== "Berawan" &&
        data.deviceStatus === "online"
          ? "blinking-content"
          : "";

      rainfall = `
      <div class="row">
        <div class="col-6">
          <div class="d-flex justify-content-between">
            <span class="reading-text">Curah Hujan :</span>
            <span class="reading-text ${blinking}" style="color: #333;font-weight:600;">${rainfallValue === "-" ? "-" : `${rainfallValue} mm`}</span>
          </div>
        </div>
        <div class="col-6">
          <div class="d-flex justify-content-between">
            <span class="reading-text">Intensitas :</span>
            <span class="reading-text ${blinking}" style="color: #333;font-weight:600;">${intensityName}</span>
          </div>
        </div>
      </div>
    `;
    }

    // 🔹 Sensor content assembly
    const contentSensor = `
    <div class="row">
      ${humidity}
      ${pressure}
      ${windDirection}
      ${solarRadiation}
      ${temperature}
      ${windSpeed}
      ${evaporation}
      ${uv}
    </div>
    ${rainfall}
  `;

    // 🔹 Reading time
    let readingAt = "";
    if (data.readingAt != null) {
      const today = moment(new Date()).format("YYYY-MMM-DD");
      const readDate = moment.parseZone(data.readingAt).format("YYYY-MMM-DD");

      if (today === readDate) {
        readingAt = `Hari Ini, <span class="text-muted">${moment
          .parseZone(data.readingAt)
          .format("HH:mm")}</span>`;
      } else {
        readingAt = moment
          .parseZone(data.readingAt)
          .locale("id")
          .format("D MMMM YYYY HH:mm");
      }
    }

    // 🔹 Footer jika offline
    let cardFooter = "";
    if (data.deviceStatus === "offline") {
      const message =
        data.readingAt == null
          ? "Alat belum mengirim data"
          : "Alat tidak aktif lebih dari 24 jam";
      cardFooter = `
      <div class="card-footer">
        <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
          <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
          ${message}
        </small>
      </div>
    `;
    }

    const awsSensorKeys = _buildAwsSensorKeys(data);

    // 🔹 Final return
    return `
    <div onclick="StationModule.showDetail(this, event)"
        class="card-station-aws"
        data-ts-id="${data.id}"
        id="card-station-aws-${data.id}"
        data-last-update="${
          data.readingAt
            ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
            : ""
        }"
        data-name="${data.name}"
        data-slug="${data.slug}" 
        data-device="${data.deviceId}"
        data-type="AWS"
        data-aws-sensors="${awsSensorKeys.join(",")}"
        data-lat="${data.latitude}"
        data-lng="${data.longitude}">
      <div class="card card-station animate__animated animate__fadeInLeft">
                ${_renderStationCardHeader(data, deviceStatus, readingAt)}
        <div class="card-body">
                    ${contentSensor}
        </div>
        ${cardFooter}
      </div>
    </div>
  `;
  };

  const _generateFmCardContent = (data) => {
    // 🔹 Device status
    let deviceStatus = `<small class="mdi mdi-circle text-danger me-1"></small>`;
    const hasReading = data.readingHour != null;

    if (hasReading) {
      const readingHour = moment.parseZone(data.readingHour);
      const currentTime = moment.parseZone();
      const diffInHours = currentTime.diff(readingHour, "hours");

      if (diffInHours < 12) {
        deviceStatus = `<small class="mdi mdi-circle text-success me-1"></small>`;
      }
    }

    // 🔹 Battery
    let battery = "";
    if (data.battery != null) {
      const batteryIcon =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryIcon} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // 🔹 Flow Meter Data (+ satuan m³)
    const flowMonth = DashboardHelpers.formatFlowMeterValue(
      data.flowMonth,
      data,
    );
    const flowTotal = DashboardHelpers.formatFlowMeterValue(
      data.flowTotal,
      data,
    );

    // 🔹 Reading time (+ zona waktu)
    const readingAt = hasReading
      ? DashboardHelpers.formatReadingTime(
          data.readingHour,
          data.timeZone || data.TimeZone || "WIB",
        )
      : "";

    // 🔹 Footer jika offline (skip jika belum ada reading — sudah ditampilkan di body)
    let cardFooter = "";
    if (data.deviceStatus === "offline" && hasReading) {
      cardFooter = `
      <div class="card-footer">
        <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
          <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
          Alat tidak aktif lebih dari 24 jam
        </small>
      </div>
    `;
    }

    const cardBodyContent = hasReading
      ? `
                    <div class="d-flex justify-content-around align-items-center reading-text">
            <div class="col-4 text-center">
              <h4>${flowMonth}</h4>
            </div>
            <div class="col-4 text-center">
              <h4>${flowTotal}</h4>
            </div>
          </div>

          <div class="d-flex justify-content-around reading-text">
            <div class="col-4 text-center">Volume Bulan Ini</div>
            <div class="col-4 text-center">Totalizer</div>
          </div>`
      : `
                    <div class="text-center py-2">
            <small class="m-0 d-flex align-items-center justify-content-center fw-normal" style="color: #ED8F2A;">
              <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
              Alat belum mengirim data
            </small>
          </div>`;

    // 🔹 Final return
    return `
    <div onclick="StationModule.showDetail(this, event)"
        class="card-station-fm"
        data-ts-id="${data.id}"
        id="card-station-fm-${data.id}"
        data-last-update="${
          data.readingHour
            ? moment(data.readingHour).format("YYYY-MM-DD HH:mm:ss")
            : ""
        }"
        data-name="${data.name}"
        data-slug="${data.slug}" 
        data-device="${data.deviceId}"
        data-lat="${data.latitude}"
        data-lng="${data.longitude}"
        data-type="FM">
      <div class="card card-station animate__animated animate__fadeInLeft">
                ${_renderStationCardHeader(data, deviceStatus, readingAt)}
        <div class="card-body">
          ${cardBodyContent}
        </div>
        ${cardFooter}
      </div>
    </div>
  `;
  };

  const _generateWaterQualityCardContent = (data) => {
    // 🔹 Device status
    let deviceStatus = `<small class="mdi mdi-circle text-danger me-1"></small>`;

    if (data.readingAt != null) {
      const readingAt = moment.parseZone(data.readingAt);
      const currentTime = moment.parseZone();
      const diffInHours = currentTime.diff(readingAt, "hours");

      if (diffInHours <= 12) {
        deviceStatus = `<small class="mdi mdi-circle text-success me-1"></small>`;
      }
    }

    // 🔹 Battery
    let battery = "";
    if (data.battery != null) {
      const batteryIcon =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryIcon} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // 🔹 Sensor Data — tampilkan sensor yang di-set true, nilai null → "-"
    const temperature = _isSensorFlagEnabled(data, "isTemperature")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Suhu :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.temperature, " °C")}</span>
        </div>
      </div>`
      : "";

    const ph = _isSensorFlagEnabled(data, "isPh", ["isPH"])
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">pH :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorValue(data.ph)}</span>
        </div>
      </div>`
      : "";

    const orp = _isSensorFlagEnabled(data, "isOrp")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">ORP :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.orp, " mV")}</span>
        </div>
      </div>`
      : "";

    const turbidity = _isSensorFlagEnabled(data, "isTurbidity")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Turbidity :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.turbidity, " NTU")}</span>
        </div>
      </div>`
      : "";

    const cod = _isSensorFlagEnabled(data, "isCod")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">COD :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.cod, " mg/L")}</span>
        </div>
      </div>`
      : "";

    const salinity = _isSensorFlagEnabled(data, "isSalinity")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Salinity :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.salinity, " g/L")}</span>
        </div>
      </div>`
      : "";

    let waterLevel = "";

    if (DashboardServerConfig.organizationCode != "ORG007") {
      waterLevel = _isSensorFlagEnabled(data, "isWaterLevel")
        ? `<div class="col-6 mb-1">
                <div class="d-flex justify-content-between">
                  <span class="reading-text">TMA :</span>
                  <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.waterLevel, ` ${data.unitDisplay ?? "m"}`)}</span>
                </div>
              </div>`
        : "";
    } else {
      const waterLevel1 = _isSensorFlagEnabled(data, "isWaterLevel")
        ? `<div class="col-6 mb-1">
                <div class="d-flex justify-content-between">
                  <span class="reading-text">TMA Hulu :</span>
                  <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.waterLevel1, ` ${data.unitDisplay ?? "m"}`)}</span>
                </div>
              </div>`
        : "";

      const waterLevel2 = _isSensorFlagEnabled(data, "isWaterLevel")
        ? `<div class="col-6 mb-1">
                <div class="d-flex justify-content-between">
                  <span class="reading-text">TMA Hilir :</span>
                  <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.waterLevel2, ` ${data.unitDisplay ?? "m"}`)}</span>
                </div>
              </div>`
        : "";

      waterLevel += waterLevel1;
      waterLevel += waterLevel2;
    }

    const doSensor = _isSensorFlagEnabled(data, "isDo")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">DO :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.do, " mg/L")}</span>
        </div>
      </div>`
      : "";

    const ammonium = _isSensorFlagEnabled(data, "isAmmonium")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Ammonium NH₄⁺ :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.ammonium, " mg/L")}</span>
        </div>
      </div>`
      : "";

    const ammonia = _isSensorFlagEnabled(data, "isAmmonia")
      ? `<div class="col-6 mb-1">
        <div class="d-flex justify-content-between">
          <span class="reading-text">Ammonia :</span>
          <span class="reading-text" style="color: #333;font-weight:600;">${_displaySensorWithUnit(data.ammonia, " mg/L")}</span>
        </div>
      </div>`
      : "";

    // 🔹 Sensor content assembly
    const contentSensor = `
    <div class="row">
      ${temperature}
      ${ph}
      ${orp}
      ${turbidity}
      ${cod}
      ${salinity}
      ${waterLevel}
      ${doSensor}
      ${ammonium}
      ${ammonia}
    </div>
  `;

    const wqSensorKeys = _buildWqSensorKeys(data).join(",");

    // 🔹 Reading time
    let readingAt = "";
    if (data.readingAt != null) {
      const today = moment(new Date()).format("YYYY-MMM-DD");
      const readDate = moment.parseZone(data.readingAt).format("YYYY-MMM-DD");

      if (today === readDate) {
        readingAt = `Hari Ini, <span class="text-muted">${moment
          .parseZone(data.readingAt)
          .format("HH:mm")}</span>`;
      } else {
        readingAt = moment
          .parseZone(data.readingAt)
          .locale("id")
          .format("D MMMM YYYY HH:mm");
      }
    }

    // 🔹 Footer jika offline
    let cardFooter = "";
    if (data.deviceStatus === "offline") {
      const message =
        data.readingAt == null
          ? "Alat belum mengirim data"
          : "Alat tidak aktif lebih dari 24 jam";
      cardFooter = `
      <div class="card-footer">
        <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
          <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
          ${message}
        </small>
      </div>
    `;
    }

    // 🔹 Final return
    return `
    <div onclick="StationModule.showDetail(this, event)"
        class="card-station-wq"
        data-ts-id="${data.id}"
        id="card-station-wq-${data.id}"
        data-wq-sensors="${wqSensorKeys}"
        data-last-update="${
          data.readingAt
            ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
            : ""
        }"
        data-name="${data.name}"
        data-slug="${data.slug}" 
        data-device="${data.deviceId}"
        data-type="WQ"
        data-lat="${data.latitude}"
        data-lng="${data.longitude}">
      <div class="card card-station animate__animated animate__fadeInLeft">
                ${_renderStationCardHeader(data, deviceStatus, readingAt)}
        <div class="card-body">
                    ${contentSensor}
        </div>
        ${cardFooter}
      </div>
    </div>
  `;
  };

  const _generateAvwrCardContent = (data) => {
    // 🔹 Device status
    let deviceStatus = `<small class="mdi mdi-circle text-danger me-1"></small>`;

    if (data.readingAt != null) {
      const readingAt = moment.parseZone(data.readingAt);
      const currentTime = moment.parseZone();
      const diffInHours = currentTime.diff(readingAt, "hours");

      if (diffInHours <= 12) {
        deviceStatus = `<small class="mdi mdi-circle text-success me-1"></small>`;
      }
    }

    // 🔹 Battery
    let battery = "";
    if (data.battery != null) {
      const batteryIcon =
        data.battery < 7
          ? "mdi-battery-charging-low"
          : "mdi-battery-charging-high";
      battery = `<span><i class="mdi ${batteryIcon} ms-1" style="color: #F69696;"></i> ${DashboardHelpers.formatNumber(data.battery)} Volt</span>`;
    }

    // 🔹 Reading time
    let readingAt = "";
    if (data.readingAt != null) {
      const today = moment(new Date()).format("YYYY-MMM-DD");
      const readDate = moment.parseZone(data.readingAt).format("YYYY-MMM-DD");

      if (today === readDate) {
        readingAt = `Hari Ini, <span class="text-muted">${moment
          .parseZone(data.readingAt)
          .format("HH:mm")}</span>`;
      } else {
        readingAt = moment
          .parseZone(data.readingAt)
          .locale("id")
          .format("D MMMM YYYY HH:mm");
      }
    }

    // 🔹 Footer jika offline
    let cardFooter = "";
    if (data.deviceStatus === "offline") {
      const message =
        data.readingAt == null
          ? "Alat belum mengirim data"
          : "Alat tidak aktif lebih dari 24 jam";
      cardFooter = `
      <div class="card-footer">
        <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
          <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
          ${message}
        </small>
      </div>
    `;
    }

    // 🔹 Final return
    return `
    <div onclick="StationModule.showDetail(this, event)"
        class="card-station-avwr"
        data-ts-id="${data.id}"
        id="card-station-avwr-${data.id}"
        data-last-update="${
          data.readingAt
            ? moment(data.readingAt).format("YYYY-MM-DD HH:mm:ss")
            : ""
        }"
        data-name="${data.name}"
        data-slug="${data.slug}" 
        data-device="${data.deviceId}"
        data-type="AVWR"
        data-lat="${data.latitude}"
        data-lng="${data.longitude}">
      <div class="card card-station animate__animated animate__fadeInLeft">
                ${_renderStationCardHeader(data, deviceStatus, readingAt)}
        <div class="card-body">
          ${DashboardHelpers.renderAvwrLastReading(data)}
        </div>
        ${cardFooter}
      </div>
    </div>
  `;
  };

  const _appendToRiverArea = (data, content, type) => {
    const riverAreaId = data.riverAreaId;
    const watershedId = data.watershedId;
    const $tabPane = $(`#tab-${type}`);

    // Create river area header if not exists in this tab
    if ($tabPane.find(`#${type}-river-area-${riverAreaId}`).length === 0) {
      $tabPane.append(`
                <h4 class="text-center m-0" style="padding: 0.7rem;font-size: 0.8rem;background-color: #2F3567;" 
                    id="${type}-river-area-${riverAreaId}">
                    <span style="color: #CDCED5;">${data.riverAreaCode}: </span>
                    <span class="ms-1" style="color: #CDCED5;">WS ${data.riverAreaName}</span>
                </h4>
                <div class="accordion custom-accordion" id="${type}-accordion-${riverAreaId}"></div>
            `);
    }

    // Create watershed section if not exists in this tab's accordion
    if ($tabPane.find(`#${type}-watershed-${watershedId}`).length === 0) {
      $tabPane.find(`#${type}-accordion-${riverAreaId}`).append(`
                <div class="card mb-0">
                    <div class="card-header card-header-watershed">
                        <h5 class="m-0 position-relative">
                            <a class="custom-accordion-title d-block d-flex align-items-center"
                                data-bs-toggle="collapse" 
                                href="#${type}-watershed-${watershedId}"
                                aria-expanded="true" 
                                aria-controls="${type}-watershed-${watershedId}">
                                <span style="color: #0ECAF0;font-weight: 600">#${data.watershedCode}: </span>
                                <span class="ms-1" style="color: #FFC10A;font-weight: 400;">${data.watershedName}</span>
                                <i class="mdi mdi-chevron-down accordion-arrow" style="color: #333;font-weight:600;"></i>
                            </a>
                        </h5>
                    </div>
                    <div id="${type}-watershed-${watershedId}" class="collapse show">
                        <div class="card-body content-watershed" style="border-bottom: 1px solid #293067;padding: 0;"></div>
                    </div>
                </div>
            `);
    }

    // Append content to watershed within this tab
    $tabPane.find(`#${type}-watershed-${watershedId} .content-watershed`).append(content);
  };

  // Public methods
  return {
    /**
     * Initialize module
     */
    init() {
      _loadFromStorage();
      _attachEventListeners();
      // Station data is loaded once via Dashboard.loadInitialData / ensureTabLoaded
    },

    /**
     * Get current state
     */
    getState() {
      return { ..._state };
    },

    /**
     * Load a tab's last-reading list once (lazy for inactive tabs).
     * @param {string} tabId
     * @param {{ force?: boolean, soft?: boolean }} [options]
     *   soft=true: AJAX refresh without shimmer, auto-select, or right-panel reload
     */
    async ensureTabLoaded(tabId, { force = false, soft = false } = {}) {
      if (!tabId || !$(`#${tabId}`).length) return;
      if (!force && _state.loadedTabs[tabId]) return;
      if (_state.loadingTabs[tabId]) return _state.loadingTabs[tabId];

      const loader = _tabLoaders[tabId];
      if (!loader) return;

      _state.loadingTabs[tabId] = Promise.resolve()
        .then(async () => {
          if (soft) _softTabLoads.add(tabId);
          try {
            await loader();
          } finally {
            _softTabLoads.delete(tabId);
          }
        })
        .then(() => {
          _state.loadedTabs[tabId] = true;
          if (
            typeof Dashboard !== "undefined" &&
            typeof Dashboard.refreshMarqueeFromStations === "function"
          ) {
            Dashboard.refreshMarqueeFromStations();
          }
          if (
            typeof Dashboard !== "undefined" &&
            typeof Dashboard.filterStations === "function"
          ) {
            Dashboard.filterStations();
          }
        })
        .finally(() => {
          delete _state.loadingTabs[tabId];
        });

      return _state.loadingTabs[tabId];
    },

    markTabLoaded(tabId) {
      if (tabId) _state.loadedTabs[tabId] = true;
    },

    resetLoadedTabs() {
      _state.loadedTabs = {};
    },

    /**
     * Soft AJAX refresh for leftbar last-reading cards.
     * Updates already-loaded tabs (and the active tab) without shimmer,
     * without auto-select/chart reload, and without map refresh.
     */
    async refreshLeftbarSoft() {
      const activeTab =
        localStorage.getItem(DashboardConstants.STORAGE_KEYS.ACTIVE_TAB) ||
        _state.activeTab ||
        "tab-awlr";

      const tabIds = new Set(
        Object.keys(_state.loadedTabs).filter((id) => _state.loadedTabs[id]),
      );
      if (activeTab) tabIds.add(activeTab);

      const targets = [...tabIds].filter(
        (tabId) => _tabLoaders[tabId] && $(`#${tabId}`).length,
      );
      if (targets.length === 0) return;

      _captureLeftbarScroll();
      await Promise.all(
        targets.map((tabId) =>
          StationModule.ensureTabLoaded(tabId, { force: true, soft: true }),
        ),
      );
      requestAnimationFrame(() => _restoreLeftbarScroll());
    },

    /**
     * Show station detail
     */
    async showDetail(element, event) {
      const $element = $(element);
      const stationId =
        $element.attr("data-ts-id") ?? $element.data("tsId") ?? $element.data("ts-id");
      const name = $element.data("name");
      const slug = $element.data("slug");
      const deviceId = $element.data("device");
      const type = $element.data("type");
      const typeUpper = String(type || "").toUpperCase();
      const isAvwr = typeUpper === "AVWR";

      const wellDept = $element.data("well-depth");
      const wellDepthUnit = $element.data("well-depth-unit");
      const wellElevation = $element.data("well-elevation");
      const hasCrossSection = DashboardHelpers.parseHasCrossSection(
        $element.attr("data-cross-section"),
      );

      // Update state
      _state.currentStationId = stationId;
      _state.currentDeviceId = deviceId;
      _state.currentStationType = type;

      // Save to localStorage
      _saveToStorage(stationId, deviceId, name);

      // Update UI
      $("#label-grafik-awlr").text(name ?? "Pilih pos");
      _updateActiveCard(type, stationId);

      // Load CCTV if module exists
      if (typeof CCTVModule !== "undefined") {
        CCTVModule.load(stationId);
      }

      // Prepare chart data
      const wqSensorsRaw =
        $element.attr("data-wq-sensors") || $element.data("wqSensors") || "";
      const wqSensors =
        typeof wqSensorsRaw === "string" && wqSensorsRaw.length > 0
          ? wqSensorsRaw.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

      const awsSensorsRaw =
        $element.attr("data-aws-sensors") || $element.data("awsSensors") || "";
      const awsSensors =
        typeof awsSensorsRaw === "string" && awsSensorsRaw.length > 0
          ? awsSensorsRaw.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

      if (typeUpper === "AWS") {
        _syncAwsSensorSelect(awsSensors);
      }

      const chartData = {
        stationId: stationId,
        deviceId: deviceId,
        type: type ? type.toLowerCase() : "",
        siaga1: $element.data("siaga1") ?? 0,
        siaga2: $element.data("siaga2") ?? 0,
        siaga3: $element.data("siaga3") ?? 0,
        wellDepth: wellDept ?? 0,
        wellDepthUnit: wellDepthUnit ?? "m",
        wellElevation: wellElevation ?? 0,
        hasCrossSection: hasCrossSection,
        wqSensors: wqSensors,
        awsSensors: awsSensors,
      };

      // AVWR has no dashboard chart — skip chart/penampang fetch.
      if (!isAvwr && typeof ChartModule !== "undefined") {
        ChartModule.loadData(chartData);
        ChartModule.loadDataPenampang(chartData);
      }

      // Load well animation for AGWLR / OW (only shown when well settings exist)
      if (typeof AnimationModule !== "undefined") {
        if (type === "AGWLR") {
          AnimationModule.loadWellData(deviceId);
        } else {
          AnimationModule.reset();
        }
      }

      if (typeof OwAnimationModule !== "undefined") {
        if (type === "OW") {
          OwAnimationModule.loadWellData(deviceId);
        } else {
          OwAnimationModule.reset();
        }
      }

      // ==== ✅ CEK KOORDINAT VALID SEBELUM SHOW MARKER ====
      const lat = parseFloat($element.data("lat"));
      const lng = parseFloat($element.data("lng"));

      const hasValidCoords =
        !isNaN(lat) &&
        !isNaN(lng) &&
        lat !== 0 &&
        lng !== 0 &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180;

      if (typeof MapModule !== "undefined") {
        await MapModule.ready();

        if (MapModule.map) {
          let fallbackStation = {
            id: stationId,
            name: name,
            slug: slug,
            deviceId: deviceId,
            type: (type || "").toUpperCase(),
            latitude: lat,
            longitude: lng,
            lastData: "-|-|-",
            deviceStatus: "offline",
          };

          // Prefer full AVWR last-reading payload so popup table matches left card.
          if (isAvwr) {
            const fromState = (_state.stations.vibratingWire || []).find(
              (s) => String(s.id) === String(stationId),
            );
            if (fromState) {
              fallbackStation = {
                ...fallbackStation,
                brandName: fromState.brandName,
                battery: fromState.battery,
                deviceStatus: fromState.deviceStatus,
                lastReading: fromState.readingAt,
                readingAt: fromState.readingAt,
                sensors: fromState.sensors,
                defaultImage: fromState.photo,
                photo: fromState.photo,
              };
            }
          }

          if (hasValidCoords) {
            const popupOpened = MapModule.showMarkerPopup(
              stationId,
              fallbackStation,
            );
            if (!popupOpened) {
              _showDetailChoiceDialog(name, slug);
            }
          } else {
            if (MapModule.infowindow) {
              MapModule.infowindow.close();
            }
            _showDetailChoiceDialog(name, slug);
          }
        }
      }
    },

    /**
     * Load AWLR stations
     */
    async loadAwlrStations() {
      try {
        _beginTabLoad("tab-awlr");

        const response = await DashboardAPI.getAwlrLastReading();

        let showTmasFooter = false;

        if (response.code === 200) {
          _prepareTabRender("tab-awlr");

          const awlrStations = _dedupeStationsByDeviceId(response.response);

          $("#total-awlr").html(awlrStations.length);

          if (awlrStations.length > 0) {
            showTmasFooter = awlrStations.some(
              (station) =>
                String(station.type || "").toUpperCase() === "TMAS",
            );
          }
          if (showTmasFooter) {
            $("#tab-awlr").append(`
                        <footer class="footer fixed-bottom text-center py-2" style="background-color: #fff;color: #000;">
                            <div class="row justify-content-center align-items-center small font-10">
                                <div class="col-auto">
                                    <span><i class="mdi mdi-arrow-up-circle text-danger font-weight-bold" style="font-size: 13px;"></i> : Tinggi Air Meningkat</span>
                                </div>
                                <div class="col-auto">
                                    <span><i class="mdi mdi-arrow-down-circle text-success font-weight-bold" style="font-size: 13px;"></i> : Tinggi Air Menurun</span>
                                </div>
                            </div>
                            <small class="fst-italic font-10" style="color: #333;font-weight:600;">*) Tanpa Status : Status Level Muka Air Belum Diatur</small>
                        </footer>
                    `);
          }

          _state.stations.awlr = awlrStations;

          awlrStations.forEach((station) => {
            const content = _generateAwlrCardContent(station);

            if (station.riverAreaId == null) {
              $("#tab-awlr").append(content);
            } else {
              _appendToRiverArea(station, content, "awlr");
            }
          });

          _finalizeTabRender("tab-awlr", _TAB_CARD_CLASS["tab-awlr"]);
        }
      } catch (error) {
        console.log("ERROR AWLR: " + error);
        if (!_isSoftTab("tab-awlr")) {
          $("#tab-awlr").html(`
                    <div class="alert alert-danger m-3">
                        <i class="mdi mdi-alert-circle-outline me-2"></i>
                        Error loading AWLR stations. Please try again.
                    </div>
                `);
        }
      }
    },

    async loadTmatStations() {
      try {
        _beginTabLoad("tab-tmat");

        const response = await DashboardAPI.getAgwlrLastReading();

        if (response.code !== 200) {
          throw new Error("Failed to load AGWLR stations");
        }

        _prepareTabRender("tab-tmat");

        const agwlrStations = _dedupeStationsByDeviceId(response.response || []);

        $("#total-tmat").html(agwlrStations.length);
        _state.stations.tmat = agwlrStations;

        agwlrStations.forEach((station) => {
          const content = _generateTmatCardContent(station);

          if (station.riverAreaId == null) {
            $("#tab-tmat").append(content);
          } else {
            _appendToRiverArea(station, content, "tmat");
          }
        });

        _finalizeTabRender("tab-tmat", _TAB_CARD_CLASS["tab-tmat"]);
      } catch (error) {
        console.error("❌ Error loading AGWLR stations:", error);
        if (!_isSoftTab("tab-tmat")) {
          $("#tab-tmat").html(`
                    <div class="alert alert-danger m-3">
                        <i class="mdi mdi-alert-circle-outline me-2"></i>
                        Error loading AGWLR stations. Please try again.
                    </div>
                `);
        }
      }
    },

    async loadOwStations() {
      try {
        _beginTabLoad("tab-ow");

        const response = await DashboardAPI.getOwLastReading();

        if (response.code !== 200) {
          throw new Error("Failed to load OW stations");
        }

        _prepareTabRender("tab-ow");

        const owStations = _dedupeStationsByDeviceId(response.response || []);
        $("#total-ow").html(owStations.length);
        _state.stations.ow = owStations;

        owStations.forEach((station) => {
          const content = _generateOwCardContent(station);
          if (station.riverAreaId == null) {
            $("#tab-ow").append(content);
          } else {
            _appendToRiverArea(station, content, "ow");
          }
        });

        _finalizeTabRender("tab-ow", _TAB_CARD_CLASS["tab-ow"]);
      } catch (error) {
        console.error("❌ Error loading OW stations:", error);
        if (!_isSoftTab("tab-ow")) {
          $("#tab-ow").html(`
                    <div class="alert alert-danger m-3">
                        <i class="mdi mdi-alert-circle-outline me-2"></i>
                        Error loading Observation Well stations. Please try again.
                    </div>
                `);
        }
      }
    },

    /**
     * Load ARR stations
     */
    async loadArrStations() {
      try {
        _beginTabLoad("tab-arr");

        const response = await DashboardAPI.getArrLastReading();

        if (response.code === 200) {
          _prepareTabRender("tab-arr");

          const arrStations = _dedupeStationsByDeviceId(response.response);

          // Update total counter
          $("#total-arr").html(arrStations.length);

          // Add footer with legend
          $("#tab-arr").append(`
                        <footer class="footer fixed-bottom text-center py-2" style="background-color: #fff;color: #000;">
                            <p class="fst-italic font-10 mb-0" style="color: #333;font-weight:600;">
                                *) Intensitas yang ditampilkan merupakan akumulasi 1 jam terakhir
                            </p>
                            <small class="fst-italic font-10" style="color: #333;font-weight:600;">
                                Sumber : <a class="fst-italic" href="/data/BMKG-Buletin-Meteorologi-Edisi-Maret-2020.pdf" target="_blank" rel="noopener">
                                    BMKG-Buletin-Meteorologi-Edisi-Maret-2020
                                </a>
                            </small>
                        </footer>
                    `);

          // Store in state
          _state.stations.arr = arrStations;

          // Render each station
          arrStations.forEach((station) => {
            const content = _generateArrCardContent(station);

            if (station.riverAreaId == null) {
              $("#tab-arr").append(content);
            } else {
              _appendToRiverArea(station, content, "arr");
            }
          });

          _finalizeTabRender("tab-arr", _TAB_CARD_CLASS["tab-arr"]);
        }
      } catch (error) {
        console.error("❌ Error loading ARR stations:", error);
        if (!_isSoftTab("tab-arr")) {
          $("#tab-arr").html(`
                    <div class="alert alert-danger m-3">
                        <i class="mdi mdi-alert-circle-outline me-2"></i>
                        Error loading ARR stations. Please try again.
                    </div>
                `);
        }
      }
    },

    /**
     * Load AWLR+ARR stations
     */
    async loadAwlrArrStations() {
      try {
        _beginTabLoad("tab-awlr-arr");

        const response = await DashboardAPI.getAwlrArrLastReading();

        if (response.code === 200) {
          _prepareTabRender("tab-awlr-arr");

          const awlrArrStations = _dedupeStationsByDeviceId(response.response);

          // Update total counter
          $("#total-awlr-arr").html(awlrArrStations.length);

          // Add combined footer
          $("#tab-awlr-arr").append(`
                        <footer class="footer fixed-bottom text-center py-2" style="background-color: #fff;color: #000;">
                            <div class="row justify-content-center align-items-center small font-10">
                                <div class="col-auto">
                                    <span><i class="mdi mdi-arrow-up-circle text-danger font-weight-bold" style="font-size: 13px;"></i> : Tinggi Air Meningkat</span>
                                </div>
                                <div class="col-auto">
                                    <span><i class="mdi mdi-arrow-down-circle text-success font-weight-bold" style="font-size: 13px;"></i> : Tinggi Air Menurun</span>
                                </div>
                            </div>
                            <small class="fst-italic font-10" style="color: #333;font-weight:600;">*) Tanpa Status : Status Level Muka Air Belum Diatur</small>
                            <br>
                            <p class="fst-italic font-10 mb-0" style="color: #333;font-weight:600;">
                                *) Intensitas yang ditampilkan merupakan akumulasi 1 jam terakhir
                            </p>
                            <small class="fst-italic font-10" style="color: #333;font-weight:600;">
                                Sumber : <a class="fst-italic" href="/data/BMKG-Buletin-Meteorologi-Edisi-Maret-2020.pdf" target="_blank" rel="noopener">
                                    BMKG-Buletin-Meteorologi-Edisi-Maret-2020
                                </a>
                            </small>
                        </footer>
                    `);

          // Store in state
          _state.stations.awlrArr = awlrArrStations;

          // Render stations (implementation similar to AWLR)
          awlrArrStations.forEach((station) => {
            const content = _generateAwlrArrCardContent(station);

            if (station.riverAreaId == null) {
              $("#tab-awlr-arr").append(content);
            } else {
              _appendToRiverArea(station, content, "awlr-arr");
            }
          });

          _finalizeTabRender(
            "tab-awlr-arr",
            _TAB_CARD_CLASS["tab-awlr-arr"],
          );
        }
      } catch (error) {
        console.error("❌ Error loading AWLR+ARR stations:", error);
      }
    },

    /**
     * Load AWS stations
     */
    async loadAwsStations() {
      try {
        _beginTabLoad("tab-aws");

        const response = await DashboardAPI.getAwsLastReading();

        if (response.code === 200) {
          _prepareTabRender("tab-aws");
          const awsStations = _dedupeStationsByDeviceId(response.response);
          $("#total-aws").html(awsStations.length);

          _state.stations.aws = awsStations;

          awsStations.forEach((station) => {
            const content = _generateAwsCardContent(station);

            if (station.riverAreaId == null) {
              $("#tab-aws").append(content);
            } else {
              _appendToRiverArea(station, content, "aws");
            }
          });

          _finalizeTabRender("tab-aws", _TAB_CARD_CLASS["tab-aws"]);
        }
      } catch (error) {
        console.error("❌ Error loading AWS stations:", error);
      }
    },

    /**
     * Load Flow Meter stations
     */
    async loadFlowMeterStations() {
      try {
        _beginTabLoad("tab-fm");

        const response = await DashboardAPI.getFlowMeterLastReading();

        if (response.code === 200) {
          _prepareTabRender("tab-fm");
          const fmStations = _dedupeStationsByDeviceId(response.response);
          $("#total-fm").html(fmStations.length);

          _state.stations.flowMeter = fmStations;

          fmStations.forEach((station) => {
            const content = _generateFmCardContent(station);

            if (station.riverAreaId == null) {
              $("#tab-fm").append(content);
            } else {
              _appendToRiverArea(station, content, "fm");
            }
          });
          _finalizeTabRender("tab-fm", _TAB_CARD_CLASS["tab-fm"]);
        }
      } catch (error) {
        console.error("❌ Error loading Flow Meter stations:", error);
      }
    },

    async loadWaterQualityStations() {
      try {
        _beginTabLoad("tab-wq");

        const response = await DashboardAPI.getWaterqualityLastReading();

        if (response.code === 200) {
          _prepareTabRender("tab-wq");
          const wqStations = _dedupeStationsByDeviceId(response.response);
          $("#total-wq").html(wqStations.length);

          _state.stations.waterQuality = wqStations;

          wqStations.forEach((station) => {
            const content = _generateWaterQualityCardContent(station);

            if (station.riverAreaId == null) {
              $("#tab-wq").append(content);
            } else {
              _appendToRiverArea(station, content, "wq");
            }
          });
          _finalizeTabRender("tab-wq", _TAB_CARD_CLASS["tab-wq"]);
        }
      } catch (error) {
        console.error("❌ Error loading Water Quality stations:", error);
      }
    },

    /**
     * Load Vibrating Wire stations
     */
    async loadVibratingWireStations() {
      try {
        _beginTabLoad("tab-avwr");

        const response = await DashboardAPI.getVibratingWireLastReading();

        if (response.code === 200) {
          _prepareTabRender("tab-avwr");
          const avwrStations = _dedupeStationsByDeviceId(response.response);
          $("#total-avwr").html(avwrStations.length);

          _state.stations.vibratingWire = avwrStations;

          avwrStations.forEach((station) => {
            const content = _generateAvwrCardContent(station);

            if (station.riverAreaId == null) {
              $("#tab-avwr").append(content);
            } else {
              _appendToRiverArea(station, content, "avwr");
            }
          });
          // Soft finalize: no auto-select on first paint (same as avoiding surprise popup).
          _finalizeTabRender("tab-avwr", null);
        }
      } catch (error) {
        console.error("❌ Error loading AVWR stations:", error);
      }
    },

    /**
     * Handle sensor change for AWS
     */
    handleSensorChange() {
      _handleSensorChange();
    },
  };
})();

// Expose to global scope for onclick events in HTML
window.StationModule = StationModule;
